<script setup lang="ts">
const { t, tm, rt, locale } = useI18n()
const config = useRuntimeConfig()
const route  = useRoute()

useReveal()

const siteUrl = config.public.siteUrl as string
const canonical = computed(() => {
  const path = route.path.endsWith('/') ? route.path : `${route.path}/`
  return `${siteUrl}${path}`
})

useHead(() => ({
  title: t('partners.meta.title'),
  meta: [
    { name: 'description',        content: t('partners.meta.description') },
    { property: 'og:title',       content: t('partners.meta.title') },
    { property: 'og:description', content: t('partners.meta.description') },
    { property: 'og:url',         content: canonical.value },
    { name: 'twitter:title',       content: t('partners.meta.title') },
    { name: 'twitter:description', content: t('partners.meta.description') },
  ],
  link: [{ rel: 'canonical', href: canonical.value }],
}))

type WhyItem      = { icon: string; title: string; text: string }
type AudienceItem = { icon: string; title: string; text: string }
type ProcessItem  = { n: string;    title: string; text: string }
type StatItem     = { value: string; label: string }
type FaqItem      = { q: string; a: string }

const whyItems = computed(() =>
  (tm('partners.why.items') as any[]).map((it) => ({
    icon: rt(it.icon), title: rt(it.title), text: rt(it.text),
  })) as WhyItem[],
)
const audienceItems = computed(() =>
  (tm('partners.audience.items') as any[]).map((it) => ({
    icon: rt(it.icon), title: rt(it.title), text: rt(it.text),
  })) as AudienceItem[],
)
const processItems = computed(() =>
  (tm('partners.process.items') as any[]).map((it) => ({
    n: rt(it.n), title: rt(it.title), text: rt(it.text),
  })) as ProcessItem[],
)
const statItems = computed(() =>
  (tm('partners.stats.items') as any[]).map((it) => ({
    value: rt(it.value), label: rt(it.label),
  })) as StatItem[],
)
const faqItems = computed(() =>
  (tm('partners.faq.items') as any[]).map((it) => ({
    q: rt(it.q), a: rt(it.a),
  })) as FaqItem[],
)

// Contact form — builds a mailto: with the user's input. No backend, no data
// leaves the device until the user hits Send in their mail client.
const form = reactive({
  name:    '',
  company: '',
  email:   '',
  role:    '',
  country: '',
  message: '',
})

const PARTNER_EMAIL = 'partners@romba.am'
const TELEGRAM_HANDLE = 'rodstvennik_partners'

const mailtoHref = computed(() => {
  const subject = `Partnership inquiry — ${form.company || form.name || 'duty-free'}`
  const lines = [
    `Name: ${form.name}`,
    `Company: ${form.company}`,
    `Role: ${form.role}`,
    `Country / airport: ${form.country}`,
    `Work email: ${form.email}`,
    '',
    'Message:',
    form.message,
    '',
    '— sent from romba.am/partners',
  ]
  return `mailto:${PARTNER_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`
})

// Open mailto: programmatically so we can preventDefault on the actual form
// submit and avoid the page navigating away.
function submit(e: Event) {
  e.preventDefault()
  if (import.meta.client) window.location.href = mailtoHref.value
}
</script>

<template>
  <div>
    <SiteNav />

    <main>
      <PageHero
        :eyebrow="t('partners.hero.eyebrow')"
        :title="t('partners.hero.title')"
        :lead="t('partners.hero.lead')"
        :cta-primary-label="t('partners.hero.ctaPrimary')"
        cta-primary-href="#contact"
        :cta-secondary-label="t('partners.hero.ctaSecondary')"
        cta-secondary-href="#process"
      />

      <!-- Why partner -->
      <section class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-14" data-reveal>
            <div class="rod-eyebrow mb-5">📈 {{ t('partners.why.eyebrow') }}</div>
            <h2 class="rod-h2 mb-5">{{ t('partners.why.title') }}</h2>
          </div>

          <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div
              v-for="(item, i) in whyItems"
              :key="i"
              class="rod-card relative flex flex-col gap-4"
              data-reveal
            >
              <div class="flex items-center gap-3">
                <div class="grid place-items-center w-12 h-12 rounded-2xl flex-shrink-0"
                     style="background: linear-gradient(140deg, rgba(245,158,11,0.18) 0%, rgba(251,146,60,0.10) 100%);
                            border: 1px solid rgba(245,158,11,0.25);
                            font-size: 22px;">
                  {{ item.icon }}
                </div>
                <h3 class="rod-h3 leading-tight" style="font-size: 1.15rem;">{{ item.title }}</h3>
              </div>
              <p class="text-[var(--color-text-muted)] leading-relaxed text-[0.95rem]">{{ item.text }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Audience -->
      <section class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-14" data-reveal>
            <div class="rod-eyebrow mb-5">🤝 {{ t('partners.audience.eyebrow') }}</div>
            <h2 class="rod-h2 mb-5">{{ t('partners.audience.title') }}</h2>
          </div>

          <div class="grid md:grid-cols-2 gap-5">
            <div
              v-for="(item, i) in audienceItems"
              :key="i"
              class="rod-card flex gap-4"
              data-reveal
            >
              <div class="grid place-items-center w-12 h-12 rounded-2xl flex-shrink-0 mt-1"
                   style="background: linear-gradient(140deg, rgba(20,197,219,0.16) 0%, rgba(31,215,154,0.08) 100%);
                          border: 1px solid rgba(20,197,219,0.20);
                          font-size: 22px;">
                {{ item.icon }}
              </div>
              <div>
                <h3 class="rod-h3 leading-tight mb-2" style="font-size: 1.1rem;">{{ item.title }}</h3>
                <p class="text-[var(--color-text-muted)] leading-relaxed text-[0.95rem]">{{ item.text }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Process -->
      <section id="process" class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-14" data-reveal>
            <div class="rod-eyebrow mb-5">🛠 {{ t('partners.process.eyebrow') }}</div>
            <h2 class="rod-h2 mb-5">{{ t('partners.process.title') }}</h2>
          </div>

          <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div
              v-for="(item, i) in processItems"
              :key="i"
              class="rod-card flex flex-col gap-3"
              data-reveal
            >
              <div class="font-mono text-base font-bold rod-gradient-text">{{ item.n }}</div>
              <h3 class="rod-h3 leading-tight" style="font-size: 1.1rem;">{{ item.title }}</h3>
              <p class="text-[var(--color-text-muted)] leading-relaxed text-[0.92rem]">{{ item.text }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Stats -->
      <section class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-12" data-reveal>
            <div class="rod-eyebrow mb-5">✨ {{ t('partners.stats.eyebrow') }}</div>
            <h2 class="rod-h2">{{ t('partners.stats.title') }}</h2>
          </div>
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-5">
            <div v-for="(s, i) in statItems" :key="i" class="rod-card text-center py-8" data-reveal>
              <div class="font-display text-4xl lg:text-5xl rod-gradient-text font-bold tracking-tight mb-1">{{ s.value }}</div>
              <div class="text-sm text-[var(--color-text-muted)] uppercase tracking-wider">{{ s.label }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact -->
      <section id="contact" class="rod-section relative overflow-hidden">
        <div class="rod-aurora" />

        <div class="rod-container relative">
          <div class="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start">
            <div data-reveal>
              <div class="rod-eyebrow mb-5">📬 {{ t('partners.contact.eyebrow') }}</div>
              <h2 class="rod-h2 mb-5">{{ t('partners.contact.title') }}</h2>
              <p class="text-lg text-[var(--color-text-muted)] mb-8 leading-relaxed">
                {{ t('partners.contact.lead') }}
              </p>

              <form class="rod-card flex flex-col gap-4" @submit="submit">
                <div class="grid sm:grid-cols-2 gap-4">
                  <label class="flex flex-col gap-1.5">
                    <span class="text-xs uppercase tracking-wider text-[var(--color-text-dim)]">{{ t('partners.contact.formNameLabel') }}</span>
                    <input v-model="form.name" type="text" required class="rod-input" />
                  </label>
                  <label class="flex flex-col gap-1.5">
                    <span class="text-xs uppercase tracking-wider text-[var(--color-text-dim)]">{{ t('partners.contact.formCompanyLabel') }}</span>
                    <input v-model="form.company" type="text" required class="rod-input" />
                  </label>
                </div>
                <div class="grid sm:grid-cols-2 gap-4">
                  <label class="flex flex-col gap-1.5">
                    <span class="text-xs uppercase tracking-wider text-[var(--color-text-dim)]">{{ t('partners.contact.formEmailLabel') }}</span>
                    <input v-model="form.email" type="email" required class="rod-input" />
                  </label>
                  <label class="flex flex-col gap-1.5">
                    <span class="text-xs uppercase tracking-wider text-[var(--color-text-dim)]">{{ t('partners.contact.formRoleLabel') }}</span>
                    <input v-model="form.role" type="text" class="rod-input" />
                  </label>
                </div>
                <label class="flex flex-col gap-1.5">
                  <span class="text-xs uppercase tracking-wider text-[var(--color-text-dim)]">{{ t('partners.contact.formCountryLabel') }}</span>
                  <input v-model="form.country" type="text" class="rod-input" />
                </label>
                <label class="flex flex-col gap-1.5">
                  <span class="text-xs uppercase tracking-wider text-[var(--color-text-dim)]">{{ t('partners.contact.formMessageLabel') }}</span>
                  <textarea v-model="form.message" rows="4" required class="rod-input" />
                </label>

                <button type="submit" class="rod-btn-primary self-start mt-2">
                  {{ t('partners.contact.formSubmit') }}
                  <span aria-hidden="true">↗</span>
                </button>

                <p class="text-xs text-[var(--color-text-dim)] leading-relaxed">
                  {{ t('partners.contact.formNote') }}
                </p>
              </form>
            </div>

            <aside class="flex flex-col gap-5" data-reveal>
              <div class="rod-card rod-card--glow">
                <div class="text-xs uppercase tracking-wider text-[var(--color-text-dim)] mb-3">
                  {{ t('partners.contact.altTitle') }}
                </div>

                <div class="flex flex-col gap-4">
                  <a :href="`mailto:${PARTNER_EMAIL}`" class="flex items-start gap-3 group">
                    <div class="grid place-items-center w-10 h-10 rounded-xl flex-shrink-0"
                         style="background: rgba(245,158,11,0.14); color: var(--color-gold-300); font-size: 18px;">✉︎</div>
                    <div>
                      <div class="text-xs uppercase tracking-wider text-[var(--color-text-dim)]">{{ t('partners.contact.emailLabel') }}</div>
                      <div class="font-mono text-sm group-hover:text-[var(--color-gold-300)] transition-colors">{{ PARTNER_EMAIL }}</div>
                    </div>
                  </a>

                  <a :href="`https://t.me/${TELEGRAM_HANDLE}`" target="_blank" rel="noopener noreferrer" class="flex items-start gap-3 group">
                    <div class="grid place-items-center w-10 h-10 rounded-xl flex-shrink-0"
                         style="background: rgba(20,197,219,0.14); color: #6FE3F1; font-size: 18px;">✈</div>
                    <div>
                      <div class="text-xs uppercase tracking-wider text-[var(--color-text-dim)]">{{ t('partners.contact.telegramLabel') }}</div>
                      <div class="font-mono text-sm group-hover:text-[#6FE3F1] transition-colors">@{{ TELEGRAM_HANDLE }}</div>
                    </div>
                  </a>

                  <div class="flex items-start gap-3 pt-4 border-t border-white/5">
                    <div class="grid place-items-center w-10 h-10 rounded-xl flex-shrink-0"
                         style="background: rgba(16,185,129,0.14); color: var(--color-verified-400); font-size: 18px;">⏱</div>
                    <div>
                      <div class="text-xs uppercase tracking-wider text-[var(--color-text-dim)]">{{ t('partners.contact.responseSlaLabel') }}</div>
                      <div class="text-sm text-[var(--color-text)]">{{ t('partners.contact.responseSlaValue') }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <!-- FAQ -->
      <section id="faq" class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-12" data-reveal>
            <div class="rod-eyebrow mb-5">❓ {{ t('partners.faq.eyebrow') }}</div>
            <h2 class="rod-h2">{{ t('partners.faq.title') }}</h2>
          </div>
          <div class="max-w-3xl mx-auto flex flex-col gap-3">
            <details v-for="(item, i) in faqItems" :key="i" class="rod-card group" data-reveal>
              <summary class="cursor-pointer list-none flex items-start justify-between gap-4">
                <span class="rod-h3 leading-snug" style="font-size: 1.05rem;">{{ item.q }}</span>
                <span class="text-[var(--color-gold-400)] text-2xl leading-none flex-shrink-0 transition-transform duration-200 group-open:rotate-45">+</span>
              </summary>
              <p class="mt-4 text-[var(--color-text-muted)] leading-relaxed text-[0.95rem]">{{ item.a }}</p>
            </details>
          </div>
        </div>
      </section>

      <!-- Final CTA -->
      <section class="rod-section">
        <div class="rod-container">
          <div class="rod-card rod-card--glow text-center py-16 px-6 lg:px-12 max-w-4xl mx-auto" data-reveal
               style="background: linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(16,185,129,0.08) 100%); border-color: rgba(245,158,11,0.3);">
            <h2 class="rod-h2 mb-5 rod-gradient-text">{{ t('partners.cta.title') }}</h2>
            <p class="text-lg text-[var(--color-text-muted)] max-w-2xl mx-auto mb-10">{{ t('partners.cta.lead') }}</p>
            <div class="flex flex-wrap justify-center gap-4">
              <a :href="`mailto:${PARTNER_EMAIL}`" class="rod-btn-primary">
                {{ t('partners.cta.primary') }}
                <span aria-hidden="true">↗</span>
              </a>
              <a :href="`https://t.me/${TELEGRAM_HANDLE}`" target="_blank" rel="noopener noreferrer" class="rod-btn-secondary">{{ t('partners.cta.secondary') }}</a>
            </div>
          </div>
        </div>
      </section>
    </main>

    <SiteFooter />
  </div>
</template>

<style scoped>
.rod-input {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 12px;
  padding: 11px 14px;
  font-size: 14px;
  color: var(--color-text);
  font-family: inherit;
  transition: border-color 180ms ease, background 180ms ease;
}
.rod-input:focus {
  outline: none;
  border-color: rgba(245,158,11,0.55);
  background: rgba(255,255,255,0.05);
}
</style>
